import React from 'react';
import { Route, Routes } from 'react-router-dom';
import '../styles/_app.scss';
import '../styles/main.scss';
import { Routes as routes } from './Routes';
import ErrorBoundary from '/01_fundaments/utilities/ErrorBoundary';
import PageTemplate from '/05_buckets/PageTemplate';
import { ImportRoot } from '@user-services/web-components';
import brandBackground from './brand-background.json';

/*=============================================
= START - Components
=============================================*/

// React code splitting allows for fast load times of a SPA.
// If you don't use this, loading would take forever !!!
// https://reactjs.org/docs/code-splitting.html

// To achieve this we use React.lazy, to dynamically fetch code when it is needed.
// https://reactjs.org/docs/code-splitting.html#reactlazy

const BrowserOutdated = React.lazy(
	() => import('/04_organisms/BrowserOutdated'),
);
const Cookie = React.lazy(
	() => import('/05_buckets/Cookie'),
);
const Notification = React.lazy(
	() => import('/02_atoms/Notification'),
);

/*=============================================
= END - Components
=============================================*/

/*=============================================
= START - App context
=============================================*/

// This creates a 'container' for data to pass to child components
// https://reactjs.org/docs/context.html#reactcreatecontext

export const AppContext = React.createContext();

/*=============================================
= END - App context
=============================================*/

function App() {
	/*=============================================
	= START - Event-based analytics
	=============================================*/

	// This will load analytics on the first render of the app.

	const [analyticsLoaded, setAnalyticsLoaded] =
		React.useState(false);
	const [analyticsScriptAdded, setAnalyticsScriptAdded] =
		React.useState(false);
	React.useEffect(() => {
		if (analyticsScriptAdded) {
			return;
		}

		setAnalyticsScriptAdded(true);

		// execute as late as possible
		// load critical assets first
		const ebaScriptUrl = `https://t${import.meta.env.MODE === 'production' ? '' : '-stag'}.vrt.be/latest/vrteba.min.js`;
		setTimeout(() => {
			let script = document.createElement('script');
			script.src = ebaScriptUrl;
			script.defer = true;
			document.body.appendChild(script);

			// onload & onerror
			script.onload = () => {
				setAnalyticsLoaded(true);
			};
			script.onerror = () => {
				setAnalyticsLoaded(false);
			};
		}, 0);
	}, [analyticsScriptAdded]);

	/*=============================================
	= END - Event-based analytics
	=============================================*/

	/*=============================================
	= START - App background image
	=============================================*/

	// This will set the background image of the App.
	// For responsiveness, we fetch 3 versions of the image.

	React.useEffect(() => {
		document
			.querySelectorAll('noscript')
			.forEach((no) => (no.style.display = 'none')); // make sure an accessibility browser doesn't see the text in noscript

		const backendDataImage =
			brandBackground[globalThis.backendData.brand] ||
			brandBackground.default;

		let { originalPagePath } = window.backendData;
		originalPagePath =
			originalPagePath || window.location.pathname;
		if (originalPagePath) {
			if (
				[
					'/subprofile_registration',
					'/subprofile_login',
					'/subprofile_logged_in_child',
				].includes(originalPagePath)
			) {
				document.body.classList.add('kids');
			}
		}

		/*
		available background profiles:

		vrt_login_bg_tablet
		1024x768/smart/filters:quality(50)

		vrt_login_bg_desktop
		1280x800/smart/filters:quality(50)

		vrt_login_bg_desktop_large
		1600x900/smart/filters:quality(50)
		*/
		const tabletImage = backendDataImage.replace(
			'/orig/',
			'/vrt_login_bg_tablet/',
		);
		const desktopImage = backendDataImage.replace(
			'/orig/',
			'/vrt_login_bg_desktop/',
		);
		const desktopLargeImage = backendDataImage.replace(
			'/orig/',
			'/vrt_login_bg_desktop_large/',
		);

		// check if url returns images
		imageExists(tabletImage, () => {
			document.documentElement.classList.add(
				'app-background--hasLoaded',
			);
			document.documentElement.style.setProperty(
				'--app-background-image-tablet',
				`url(${tabletImage})`,
			);
		});

		imageExists(desktopImage, () => {
			document.documentElement.classList.add(
				'app-background--hasLoaded',
			);
			document.documentElement.style.setProperty(
				'--app-background-image-desktop',
				`url(${desktopImage})`,
			);
		});

		imageExists(desktopLargeImage, () => {
			document.documentElement.classList.add(
				'app-background--hasLoaded',
			);
			document.documentElement.style.setProperty(
				'--app-background-image-desktop-large',
				`url(${desktopLargeImage})`,
			);
		});
	}, []);

	// Update desktop background based on GrowthBook feature

	function imageExists(imageUrl, callBack) {
		const imageData = new Image();
		imageData.onload = function () {
			callBack();
		};
		imageData.src = imageUrl;
	}

	/*=============================================
	= END - App background image
	=============================================*/

	/*=============================================
	= START - Notification
	=============================================*/

	// This code allows components to show notifications
	// showNotification() is passed the child components through AppContext

	const [renderNotification, setRenderNotification] =
		React.useState(false);
	const [notificationMessage, setNotificationMessage] =
		React.useState('');
	const [notificationStatus, setNotificationStatus] =
		React.useState('success');
	const [notificationNoTimer, setNotificationNoTimer] =
		React.useState(false);

	function showNotification(
		notificationType,
		message,
		noTimer,
	) {
		setRenderNotification(true);
		setNotificationMessage(message);
		setNotificationStatus(notificationType);
		setNotificationNoTimer(!!noTimer);
	}

	function hideNotification() {
		setRenderNotification(false);
		setNotificationMessage('');
		setNotificationStatus('success');
		setNotificationNoTimer(true);
	}

	/*=============================================
	= END - Notification
	=============================================*/

	/**
	 * send a message to the backendData to use on another page
	 *
	 * @param type	'info'|'success'|'error'
	 * @param title
	 * @param text
	 * @returns
	 *
	 * @example
	 * ```
	 * Write me later.
	 * ```
	 */
	function setBackendMessage(type, title, text) {
		window.backendData.transferMessage = {
			type,
			title,
			text,
		};
	}
	function getBackendMessage() {
		const result = window.backendData.transferMessage;
		window.backendData.transferMessage = null;
		return result;
	}

	/*=============================================
	= START - AppContext
	=============================================*/

	// Here we set all the data, functions, etc.
	// that we would like to make available for child components.

	const appContextValue = {
		analyticsLoaded,
		showNotification,
		setBackendMessage,
		getBackendMessage,
	};

	/*=============================================
	= END - AppContext
	=============================================*/

	return (
		<AppContext.Provider value={appContextValue}>
			<ImportRoot
				className={`app ${brandBackground[globalThis.backendData.brand] ? globalThis.backendData.brand : ''} ${window.backendData.styleHint === 'native' ? 'inApp' : ''}`}
			>
				{/* OutdatedBrowser */}
				<div aria-live="polite">
					<ErrorBoundary silent>
						<React.Suspense fallback={''}>
							<BrowserOutdated />
						</React.Suspense>
					</ErrorBoundary>
				</div>

				{/* Notification */}
				<div aria-live="polite" aria-atomic="true">
					{renderNotification && (
						<ErrorBoundary silent>
							<React.Suspense fallback={''}>
								<Notification
									hideNotification={hideNotification}
									message={notificationMessage}
									type={notificationStatus}
									noTimer={notificationNoTimer}
								/>
							</React.Suspense>
						</ErrorBoundary>
					)}
				</div>
				{/* App */}
				<div className="app__container">
					{/* <div className="app__row"> */}
					<main className="app__main" aria-live="polite">
						<Routes>
							{routes.map((route, i) => (
								<Route
									exact={route.exact}
									key={i}
									path={route.path}
									element={
										<PageTemplate routes={route.routes} />
									}
								/>
							))}
						</Routes>
					</main>
					{/* </div> */}
				</div>
			</ImportRoot>
			{/* Cookie */}
			<div
				className={`${window.backendData.styleHint === 'native' ? 'inApp' : ''}`}
				aria-live="polite"
			>
				<ErrorBoundary silent>
					<React.Suspense fallback={''}>
						<Cookie />
					</React.Suspense>
				</ErrorBoundary>
			</div>
		</AppContext.Provider>
	);
}

export default App;
